import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../services/api';

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

interface ValidationRow {
  row: number;
  isValid: boolean;
  errors: string[];
  preview: {
    text: string;
    scheduled_for: string;
    platforms: string[];
    status: string;
    post_type: string;
  };
}

interface ValidationResult {
  total: number;
  valid: number;
  invalid: number;
  hasErrors: boolean;
  detectedFormat: 'csv' | 'json' | 'ndjson' | 'md';
  rows: ValidationRow[];
}

interface ImportResult {
  success: boolean;
  total: number;
  imported: number;
  failed: number;
  batch_id?: string;
  batch_display_id?: string;
  rollback_until?: string;
  errors: Array<{ row: number; message: string }>;
  posts: Array<{ row: number; id: string; display_id: string; text: string }>;
}

interface BatchUploadProps {
  projectId: string;
  onImportComplete: () => void;
  onClose: () => void;
}

type ImportFormat = 'csv' | 'json' | 'ndjson' | 'md';

export function BatchUpload({ projectId, onImportComplete, onClose }: BatchUploadProps) {
  const { t } = useTranslation(['posts', 'common']);
  const [step, setStep] = useState<'upload' | 'preview' | 'result'>('upload');
  const [content, setContent] = useState('');
  const [fileName, setFileName] = useState('');
  const [detectedFormat, setDetectedFormat] = useState<ImportFormat | null>(null);
  const [validation, setValidation] = useState<ValidationResult | null>(null);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const formatLabels: Record<ImportFormat, { name: string; icon: string; ext: string; desc: string }> = {
    csv: { name: 'CSV', icon: '', ext: '.csv', desc: t('posts:batch.import.csvDescription') },
    json: { name: 'JSON', icon: '', ext: '.json', desc: t('posts:batch.import.jsonDescription') },
    ndjson: { name: 'NDJSON', icon: '', ext: '.ndjson', desc: t('posts:batch.import.ndjsonDescription') },
    md: { name: 'Markdown', icon: '', ext: '.md', desc: t('posts:batch.import.markdownDescription') },
  };

  const isValidExtension = (name: string) => {
    const ext = name.toLowerCase().split('.').pop();
    return ext === 'csv' || ext === 'json' || ext === 'ndjson' || ext === 'jsonl' || ext === 'md' || ext === 'markdown';
  };

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!isValidExtension(file.name)) {
      setError(t('posts:batch.import.fileError'));
      return;
    }

    setFileName(file.name);
    setError('');

    const reader = new FileReader();
    reader.onload = (event) => {
      const fileContent = event.target?.result as string;
      setContent(fileContent);
    };
    reader.onerror = () => {
      setError(t('posts:batch.import.readError'));
    };
    reader.readAsText(file, 'UTF-8');
  }, [t]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (!file) return;

    if (!isValidExtension(file.name)) {
      setError(t('posts:batch.import.fileError'));
      return;
    }

    setFileName(file.name);
    setError('');

    const reader = new FileReader();
    reader.onload = (event) => {
      const fileContent = event.target?.result as string;
      setContent(fileContent);
    };
    reader.readAsText(file, 'UTF-8');
  }, [t]);

  const handleValidate = async () => {
    if (!content) {
      setError(t('posts:batch.import.noFileSelected'));
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await api.post<ApiResponse<ValidationResult>>('/posts/batch/validate', {
        content,
        project_id: projectId,
        filename: fileName,
      });

      if (response.success && response.data) {
        setValidation(response.data);
        setDetectedFormat(response.data.detectedFormat);
        setStep('preview');
      } else {
        setError(response.error || t('posts:batch.import.validationError'));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('posts:batch.import.validationFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleImport = async () => {
    if (!content) return;

    setLoading(true);
    setError('');

    try {
      const response = await api.post<ApiResponse<ImportResult>>('/posts/batch/import', {
        content,
        project_id: projectId,
        filename: fileName,
      });

      if (response.data) {
        setImportResult(response.data);
        setStep('result');

        if (response.data.imported > 0) {
          onImportComplete();
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('posts:batch.import.importFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadTemplate = (format: ImportFormat) => {
    const endpoint = format === 'md' ? 'markdown' : format;
    window.open(`/api/batches/templates/${endpoint}`, '_blank');
  };

  const formatDate = (isoString: string) => {
    return new Date(isoString).toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b flex items-center justify-between">
          <h2 className="text-xl font-semibold">
            {step === 'upload' && t('posts:batch.import.title')}
            {step === 'preview' && (
              <>
                {t('posts:batch.import.preview')}
                {detectedFormat && (
                  <span className="ml-2 text-sm font-normal text-gray-500">
                    ({formatLabels[detectedFormat].name} {t('posts:batch.import.formatDetected', { format: '' }).replace('{{format}}', '').trim()})
                  </span>
                )}
              </>
            )}
            {step === 'result' && t('posts:batch.import.result')}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
          >
            &times;
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded">
              {error}
            </div>
          )}

          {/* Step 1: Upload */}
          {step === 'upload' && (
            <div className="space-y-6">
              {/* Format Tabs - Template Downloads */}
              <div className="flex flex-wrap gap-2 border-b pb-3">
                <span className="text-sm text-gray-500 self-center mr-2">{t('posts:batch.import.templates')}:</span>
                {(['csv', 'json', 'ndjson', 'md'] as ImportFormat[]).map((format) => (
                  <button
                    key={format}
                    onClick={() => handleDownloadTemplate(format)}
                    className="px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 rounded-full flex items-center gap-1.5 transition-colors"
                  >
                    <span>{formatLabels[format].name}</span>
                  </button>
                ))}
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h3 className="font-medium text-blue-900 mb-2">{t('posts:batch.import.supportedFormats')}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 text-sm">
                  <div className="bg-white rounded p-3">
                    <div className="font-medium flex items-center gap-2 mb-1">
                      <span>CSV</span>
                    </div>
                    <p className="text-gray-600 text-xs">
                      {t('posts:batch.import.csvDescription')}
                    </p>
                  </div>
                  <div className="bg-white rounded p-3">
                    <div className="font-medium flex items-center gap-2 mb-1">
                      <span>JSON</span>
                    </div>
                    <p className="text-gray-600 text-xs">
                      {t('posts:batch.import.jsonDescription')}
                    </p>
                  </div>
                  <div className="bg-white rounded p-3 ring-2 ring-purple-200">
                    <div className="font-medium flex items-center gap-2 mb-1">
                      <span>NDJSON</span>
                      <span className="text-[10px] bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded">{t('posts:batch.import.recommended')}</span>
                    </div>
                    <p className="text-gray-600 text-xs">
                      {t('posts:batch.import.ndjsonDescription')}
                    </p>
                  </div>
                  <div className="bg-white rounded p-3">
                    <div className="font-medium flex items-center gap-2 mb-1">
                      <span>Markdown</span>
                    </div>
                    <p className="text-gray-600 text-xs">
                      {t('posts:batch.import.markdownDescription')}
                    </p>
                  </div>
                </div>
              </div>

              <div
                onDrop={handleDrop}
                onDragOver={(e) => e.preventDefault()}
                className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-blue-400 transition-colors"
              >
                <input
                  type="file"
                  accept=".csv,.json,.ndjson,.jsonl,.md,.markdown"
                  onChange={handleFileSelect}
                  className="hidden"
                  id="file-upload"
                />
                <label htmlFor="file-upload" className="cursor-pointer">
                  <div className="text-4xl mb-3">&#128193;</div>
                  <p className="text-lg font-medium text-gray-700">
                    {t('posts:batch.import.dragDropTitle')}
                  </p>
                  <p className="text-sm text-gray-500 mt-1">
                    {t('posts:batch.import.dragDropSubtitle')}
                  </p>
                </label>
              </div>

              {fileName && (
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded">
                  <span className="text-2xl">
                    {fileName.endsWith('.ndjson') || fileName.endsWith('.jsonl') ? '&#128196;' :
                     fileName.endsWith('.json') ? '&#128203;' :
                     fileName.endsWith('.md') ? '&#128221;' : '&#128202;'}
                  </span>
                  <div className="flex-1">
                    <p className="font-medium">{fileName}</p>
                    <p className="text-sm text-gray-500">
                      {t('posts:batch.import.fileSelected', { characters: content.length.toLocaleString() })}
                    </p>
                  </div>
                  <button
                    onClick={() => {
                      setContent('');
                      setFileName('');
                    }}
                    className="text-red-500 hover:text-red-700"
                  >
                    {t('posts:batch.import.removeFile')}
                  </button>
                </div>
              )}

              {/* LLM Hint */}
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                <h3 className="font-medium text-purple-900 mb-2 flex items-center gap-2">
                  <span>&#129302;</span> {t('posts:batch.import.llmTip')}
                </h3>
                <p className="text-sm text-purple-800 mb-2">
                  {t('posts:batch.import.llmTipDescription')}
                </p>
                <div className="flex gap-3">
                  <button
                    onClick={() => handleDownloadTemplate('ndjson')}
                    className="text-sm text-purple-600 hover:text-purple-800 underline"
                  >
                    {t('posts:batch.import.ndjsonTemplate')}
                  </button>
                  <button
                    onClick={() => window.open('/api/batches/templates/llm-prompt', '_blank')}
                    className="text-sm text-purple-600 hover:text-purple-800 underline"
                  >
                    {t('posts:batch.import.llmPromptTemplate')}
                  </button>
                </div>
              </div>

              {/* Large file info */}
              <div className="text-xs text-gray-500 flex items-center gap-2">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>
                  {t('posts:batch.import.maxFileSize')}
                </span>
              </div>
            </div>
          )}

          {/* Step 2: Preview */}
          {step === 'preview' && validation && (
            <div className="space-y-4">
              <div className="flex gap-4">
                <div className="flex-1 p-4 bg-gray-50 rounded text-center">
                  <div className="text-2xl font-bold">{validation.total}</div>
                  <div className="text-sm text-gray-500">{t('posts:batch.import.total')}</div>
                </div>
                <div className="flex-1 p-4 bg-green-50 rounded text-center">
                  <div className="text-2xl font-bold text-green-600">{validation.valid}</div>
                  <div className="text-sm text-gray-500">{t('posts:batch.import.valid')}</div>
                </div>
                <div className="flex-1 p-4 bg-red-50 rounded text-center">
                  <div className="text-2xl font-bold text-red-600">{validation.invalid}</div>
                  <div className="text-sm text-gray-500">{t('posts:batch.import.invalid')}</div>
                </div>
              </div>

              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left">{t('posts:batch.import.row')}</th>
                      <th className="px-3 py-2 text-left">{t('posts:batch.import.status')}</th>
                      <th className="px-3 py-2 text-left">{t('posts:batch.import.text')}</th>
                      <th className="px-3 py-2 text-left">{t('posts:batch.import.date')}</th>
                      <th className="px-3 py-2 text-left">{t('posts:batch.import.platforms')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {validation.rows.map((row) => (
                      <tr
                        key={row.row}
                        className={row.isValid ? 'bg-white' : 'bg-red-50'}
                      >
                        <td className="px-3 py-2 font-mono">{row.row}</td>
                        <td className="px-3 py-2">
                          {row.isValid ? (
                            <span className="text-green-600">&#10003;</span>
                          ) : (
                            <span
                              className="text-red-600 cursor-help"
                              title={row.errors.join('\n')}
                            >
                              &#10007;
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2 max-w-xs truncate">
                          {row.isValid ? row.preview.text : (
                            <span className="text-red-600 text-xs">
                              {row.errors.join('; ')}
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap">
                          {row.isValid && formatDate(row.preview.scheduled_for)}
                        </td>
                        <td className="px-3 py-2">
                          {row.isValid && row.preview.platforms.map((p) => (
                            <span
                              key={p}
                              className={`inline-block px-2 py-0.5 rounded text-xs mr-1 ${
                                p === 'facebook' ? 'bg-blue-100 text-blue-800' : 'bg-pink-100 text-pink-800'
                              }`}
                            >
                              {p}
                            </span>
                          ))}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {validation.invalid > 0 && (
                <div className="p-3 bg-yellow-50 border border-yellow-200 rounded text-yellow-800 text-sm">
                  <strong>{t('common:warning')}:</strong> {t('posts:batch.import.validNote', { valid: validation.valid, invalid: validation.invalid })}
                </div>
              )}
            </div>
          )}

          {/* Step 3: Result */}
          {step === 'result' && importResult && (
            <div className="space-y-4">
              <div className={`p-6 rounded-lg text-center ${
                importResult.success ? 'bg-green-50' : 'bg-yellow-50'
              }`}>
                <div className="text-4xl mb-2">
                  {importResult.success ? '&#10004;' : '&#9888;'}
                </div>
                <h3 className="text-xl font-semibold mb-1">
                  {t('posts:batch.import.postsImported', { count: importResult.imported })}
                </h3>
                {importResult.failed > 0 && (
                  <p className="text-gray-600">
                    {t('posts:batch.import.failed', { count: importResult.failed })}
                  </p>
                )}
                {importResult.batch_display_id && (
                  <p className="text-sm text-gray-500 mt-2">
                    {t('posts:batch.import.batchId')}: <span className="font-mono">{importResult.batch_display_id}</span>
                  </p>
                )}
                {importResult.rollback_until && (
                  <p className="text-xs text-gray-400 mt-1">
                    {t('posts:batch.import.rollbackUntil')}: {formatDate(importResult.rollback_until)}
                  </p>
                )}
              </div>

              {importResult.posts.length > 0 && (
                <div className="border rounded-lg overflow-hidden">
                  <div className="bg-gray-50 px-4 py-2 font-medium">
                    {t('posts:batch.import.importedPosts')}
                  </div>
                  <div className="divide-y max-h-60 overflow-auto">
                    {importResult.posts.map((post) => (
                      <div key={post.id} className="px-4 py-2 flex items-center gap-3">
                        <span className="text-xs font-mono text-gray-400">
                          {post.display_id}
                        </span>
                        <span className="flex-1 truncate">{post.text}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {importResult.errors.length > 0 && (
                <div className="border border-red-200 rounded-lg overflow-hidden">
                  <div className="bg-red-50 px-4 py-2 font-medium text-red-800">
                    {t('posts:batch.import.errors')}
                  </div>
                  <div className="divide-y max-h-40 overflow-auto">
                    {importResult.errors.map((err, i) => (
                      <div key={i} className="px-4 py-2 text-sm text-red-600">
                        {t('posts:batch.import.rowError', { row: err.row })}: {err.message}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t flex justify-between">
          {step === 'upload' && (
            <>
              <button
                onClick={onClose}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                {t('common:cancel')}
              </button>
              <button
                onClick={handleValidate}
                disabled={!content || loading}
                className="px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? t('posts:batch.import.checking') : t('posts:batch.import.toPreview')}
              </button>
            </>
          )}

          {step === 'preview' && (
            <>
              <button
                onClick={() => setStep('upload')}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                {t('posts:batch.import.back')}
              </button>
              <button
                onClick={handleImport}
                disabled={loading || (validation?.valid === 0)}
                className="px-6 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? t('posts:batch.import.importing') : t('posts:batch.import.importCount', { count: validation?.valid || 0 })}
              </button>
            </>
          )}

          {step === 'result' && (
            <button
              onClick={onClose}
              className="ml-auto px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              {t('common:close')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
